import type { RevitOperationProgressSnapshot } from '../../api/grpc/operationProgressClient';
import type { ConnectorProjectParameterUndoEntry } from '../../types/parameterSetupUndo';

export interface ProjectParameterBatchCounts {
  createdCount: number;
  updatedCount: number;
  existingCount: number;
  conflictingCount: number;
  failedCount: number;
  bindingConflictExistingCount?: number;
}

export interface ProjectParameterOperationDiagnostics {
  failedParameters: string[];
  conflictingParameters: string[];
  failureMessages: string[];
}

export type ProjectBindingConflictMode = 'fail' | 'treatAsExisting';

export interface ProjectParameterFeedbackOptions {
  bindingConflictMode?: ProjectBindingConflictMode;
}

export function resolveScopedBindingFailureCount(
  hostFailedCount: number,
  missingParameterCount: number
): number {
  return Math.max(hostFailedCount, missingParameterCount);
}

type ProjectParameterTerminalStatus =
  | 'Created'
  | 'Updated'
  | 'AlreadyExistsCompatible'
  | 'Conflict'
  | 'Failed';

interface ProjectParameterHostResult {
  parameter?: string;
  status?: ProjectParameterTerminalStatus | string;
  success?: boolean;
  message?: string;
  error?: string;
}

interface ProjectParameterOperationResult {
  success: boolean;
  data?: {
    addedParametersCount?: number;
    message?: string;
    error?: string;
    results?: ProjectParameterHostResult[];
    undoEntries?: ConnectorProjectParameterUndoEntry[];
  };
  error?: string;
}

interface ParsedFileCreationResult {
  success: boolean;
  data?: {
    successCount?: number;
    failureCount?: number;
    totalCount?: number;
    results?: ProjectParameterHostResult[];
    undoEntries?: ConnectorProjectParameterUndoEntry[];
  };
  error?: string;
}

interface ProjectParameterAdditionOutcome {
  success: boolean;
  addedCount: number;
  updatedCount: number;
  existingCount: number;
  failedCount: number;
  conflictingCount: number;
  bindingConflictExistingCount?: number;
  hasSystemFamilies: false;
  hasLoadableFamilies: false;
  queued: false;
  requestedCount: number;
  failedParameters: string[];
  conflictingParameters: string[];
  failureMessages: string[];
  projectUndoEntries?: ConnectorProjectParameterUndoEntry[];
  error?: string;
}

function createEmptyProjectParameterBatchCounts(): ProjectParameterBatchCounts {
  return {
    createdCount: 0,
    updatedCount: 0,
    existingCount: 0,
    conflictingCount: 0,
    failedCount: 0,
  };
}

export function normalizeProjectParameterProgress(
  progress: RevitOperationProgressSnapshot,
  _mode: ProjectBindingConflictMode | undefined
): RevitOperationProgressSnapshot {
  return progress;
}

export function treatProjectBindingConflictsAsExisting(
  counts: ProjectParameterBatchCounts,
  _errorText: string | null | undefined,
  _mode: ProjectBindingConflictMode | undefined
): ProjectParameterBatchCounts {
  return counts;
}

export function parseProjectParameterBatchMessage(
  message: string | undefined
): ProjectParameterBatchCounts | null {
  if (!message) {
    return null;
  }

  const match = message.match(
    /Created\s+(\d+)\s+project parameters,\s+updated\s+(\d+),\s+(\d+)\s+already existed,\s+(\d+)\s+conflicts,\s+(\d+)\s+failed/i
  );
  if (!match) {
    return null;
  }

  return {
    createdCount: Number(match[1] ?? 0),
    updatedCount: Number(match[2] ?? 0),
    existingCount: Number(match[3] ?? 0),
    conflictingCount: Number(match[4] ?? 0),
    failedCount: Number(match[5] ?? 0),
  };
}

export function summarizeProjectParameterOperationResults(
  results: ProjectParameterHostResult[] | undefined,
  _options?: ProjectParameterFeedbackOptions
): ProjectParameterBatchCounts {
  const counts = createEmptyProjectParameterBatchCounts();

  (results ?? []).forEach((result) => {
    switch (result.status) {
      case 'Created':
        counts.createdCount += 1;
        break;
      case 'Updated':
        counts.updatedCount += 1;
        break;
      case 'AlreadyExistsCompatible':
        counts.existingCount += 1;
        break;
      case 'Conflict':
        counts.conflictingCount += 1;
        break;
      case 'Failed':
        counts.failedCount += 1;
        break;
    }
  });

  return counts;
}

export function summarizeProjectParameterOperationDiagnostics(
  results: ProjectParameterHostResult[] | undefined,
  _options?: ProjectParameterFeedbackOptions
): ProjectParameterOperationDiagnostics {
  const diagnostics: ProjectParameterOperationDiagnostics = {
    failedParameters: [],
    conflictingParameters: [],
    failureMessages: [],
  };

  (results ?? []).forEach((result) => {
    if (result.status !== 'Conflict' && result.status !== 'Failed') {
      return;
    }

    const parameter = result.parameter?.trim() || 'Unknown parameter';
    const message = result.error?.trim() || result.message?.trim() || 'Unknown parameter setup failure';
    const detail = `${parameter}: ${message}`;

    if (result.status === 'Conflict') {
      diagnostics.conflictingParameters.push(detail);
    } else {
      diagnostics.failedParameters.push(detail);
      diagnostics.failureMessages.push(message);
    }
  });

  return diagnostics;
}

export function buildProjectParameterOperationFailureMessage(
  batchCounts: ProjectParameterBatchCounts,
  diagnostics: ProjectParameterOperationDiagnostics,
  fallback?: string
): string | undefined {
  const issueCount = batchCounts.failedCount + batchCounts.conflictingCount;
  if (issueCount === 0) {
    return undefined;
  }

  const issueLabel = issueCount === 1 ? 'parameter' : 'parameters';
  const firstDetail =
    diagnostics.failedParameters[0] ||
    diagnostics.conflictingParameters[0] ||
    fallback?.trim();
  const remainingCount = Math.max(
    0,
    diagnostics.failedParameters.length + diagnostics.conflictingParameters.length - 1
  );
  const suffix = remainingCount > 0 ? ` (+${remainingCount} more)` : '';

  if (!firstDetail) {
    return `${issueCount} project ${issueLabel} failed during setup.`;
  }

  if (firstDetail === fallback?.trim()) {
    return `${issueCount} project ${issueLabel} failed during setup. Revit reported: ${firstDetail}`;
  }

  return `${issueCount} project ${issueLabel} failed during setup: ${firstDetail}${suffix}`;
}

export function buildProjectParameterAdditionOutcome(input: {
  result: ProjectParameterOperationResult;
  requestedCount: number;
  requestedParameters?: string[];
  bindingConflictMode?: ProjectBindingConflictMode;
}): ProjectParameterAdditionOutcome {
  const { result, requestedCount, requestedParameters, bindingConflictMode } = input;
  const responseData = result.data;
  const hostError = result.error?.trim() || responseData?.error?.trim() || (
    result.success ? undefined : 'Revit did not complete project parameter setup.'
  );

  if (!responseData) {
    return buildMissingHostDataOutcome(requestedCount, hostError || 'Failed to add parameters');
  }

  return buildTerminalResultOutcome({
    results: responseData.results ?? [],
    requestedCount,
    requestedParameters,
    bindingConflictMode,
    hostError,
    blockingFallback: result.error || responseData.error || responseData.message,
    projectUndoEntries: responseData.undoEntries,
  });
}

export function buildParsedFileCreationOutcome(input: {
  result: ParsedFileCreationResult;
  requestedCount: number;
  requestedParameters?: string[];
  bindingConflictMode?: ProjectBindingConflictMode;
}): ProjectParameterAdditionOutcome {
  const { result, requestedCount, requestedParameters, bindingConflictMode } = input;

  if (!result.data) {
    return buildMissingHostDataOutcome(
      requestedCount,
      result.error || 'Failed to add instance project parameters'
    );
  }

  return buildTerminalResultOutcome({
    results: result.data.results ?? [],
    requestedCount,
    requestedParameters,
    bindingConflictMode,
    hostError: result.error?.trim() || (
      result.success ? undefined : 'Revit did not complete connector parameter setup.'
    ),
    blockingFallback: result.error,
    projectUndoEntries: result.data.undoEntries,
  });
}

// Counts and success come ONLY from the identity-matched per-item results
// channel; summary text (`message`, `addedParametersCount`) is never counted.
function buildTerminalResultOutcome(input: {
  results: ProjectParameterHostResult[];
  requestedCount: number;
  requestedParameters?: string[];
  bindingConflictMode?: ProjectBindingConflictMode;
  hostError: string | undefined;
  blockingFallback: string | undefined;
  projectUndoEntries?: ConnectorProjectParameterUndoEntry[];
}): ProjectParameterAdditionOutcome {
  const {
    results,
    requestedCount,
    requestedParameters,
    bindingConflictMode,
    hostError,
    blockingFallback,
    projectUndoEntries,
  } = input;
  const options = { bindingConflictMode };
  const batchCounts = summarizeProjectParameterOperationResults(results, options);
  const diagnostics = summarizeProjectParameterOperationDiagnostics(results, options);
  const blockingError = buildProjectParameterOperationFailureMessage(
    batchCounts,
    diagnostics,
    blockingFallback
  );
  const terminalIdentityError = buildTerminalIdentityError(requestedParameters, results);
  const terminalStatusError = buildTerminalStatusError(results);
  const missingTerminalCount = requestedCount - results.length;
  const error =
    blockingError ??
    terminalStatusError ??
    hostError ??
    terminalIdentityError ??
    (missingTerminalCount > 0
      ? `${missingTerminalCount} project parameter(s) did not report a terminal outcome.`
      : undefined);

  return {
    success: !error,
    addedCount: batchCounts.createdCount,
    updatedCount: batchCounts.updatedCount,
    existingCount: batchCounts.existingCount,
    failedCount: batchCounts.failedCount,
    conflictingCount: batchCounts.conflictingCount,
    bindingConflictExistingCount: batchCounts.bindingConflictExistingCount,
    hasSystemFamilies: false,
    hasLoadableFamilies: false,
    queued: false,
    requestedCount,
    failedParameters: diagnostics.failedParameters,
    conflictingParameters: diagnostics.conflictingParameters,
    failureMessages: diagnostics.failureMessages,
    ...(projectUndoEntries && projectUndoEntries.length > 0
      ? { projectUndoEntries }
      : {}),
    error,
  };
}

function buildMissingHostDataOutcome(requestedCount: number, error: string): ProjectParameterAdditionOutcome {
  return {
    success: false,
    addedCount: 0,
    updatedCount: 0,
    existingCount: 0,
    failedCount: 0,
    conflictingCount: 0,
    hasSystemFamilies: false,
    hasLoadableFamilies: false,
    queued: false,
    requestedCount,
    failedParameters: [],
    conflictingParameters: [],
    failureMessages: [],
    error,
  };
}

function buildTerminalStatusError(
  results: ProjectParameterHostResult[]
): string | undefined {
  const missing: string[] = [];
  const unknown: string[] = [];
  const mismatched: string[] = [];

  results.forEach((result) => {
    const parameter = result.parameter?.trim() || 'unnamed result';
    switch (result.status) {
      case 'Created':
      case 'Updated':
      case 'AlreadyExistsCompatible':
        if (result.success !== true) {
          mismatched.push(parameter);
        }
        break;
      case 'Conflict':
      case 'Failed':
        if (result.success !== false) {
          mismatched.push(parameter);
        }
        break;
      case undefined:
      case '':
        missing.push(parameter);
        break;
      default:
        unknown.push(parameter);
        break;
    }
  });

  if (missing.length === 0 && unknown.length === 0 && mismatched.length === 0) {
    return undefined;
  }

  const details = [
    missing.length > 0 ? `missing status: ${missing.join(', ')}` : '',
    unknown.length > 0 ? `unknown status: ${unknown.join(', ')}` : '',
    mismatched.length > 0 ? `status/success mismatch: ${mismatched.join(', ')}` : '',
  ].filter(Boolean).join('; ');
  return `Connector parameter terminal outcomes were invalid (${details}).`;
}

function buildTerminalIdentityError(
  requestedParameters: string[] | undefined,
  results: ProjectParameterHostResult[]
): string | undefined {
  if (!requestedParameters) {
    return undefined;
  }

  const normalize = (value: string | undefined) => value?.trim().toLowerCase() ?? '';
  const remaining = new Map<string, string>();
  requestedParameters.forEach((parameter) => {
    const key = normalize(parameter);
    if (key) {
      remaining.set(key, parameter.trim());
    }
  });

  const unexpected: string[] = [];
  results.forEach((result) => {
    const key = normalize(result.parameter);
    if (!key || !remaining.delete(key)) {
      unexpected.push(result.parameter?.trim() || 'unnamed result');
    }
  });

  const missing = Array.from(remaining.values());
  if (missing.length === 0 && unexpected.length === 0) {
    return undefined;
  }

  const details = [
    missing.length > 0 ? `missing: ${missing.join(', ')}` : '',
    unexpected.length > 0 ? `unexpected or duplicate: ${unexpected.join(', ')}` : '',
  ].filter(Boolean).join('; ');
  return `Connector parameter terminal outcomes did not match the request (${details}).`;
}
